import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Tooltip,
  Alert,
  CircularProgress,
  Stack,
  Tabs,
  Tab,
  Checkbox,
  Link,
  Snackbar,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { FlagUS, FlagUK, FlagJP, FlagCA, MarketFlag } from './FlagIcons';
import RefreshIcon from '@mui/icons-material/Refresh';
import axios from 'axios';

// DnD Kit imports
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import GoldIngotLoader from './GoldIngotLoader';

// Market configuration constants
const MARKETS = {
  US: {
    code: 'US',
    name: 'United States',
    domain: 'www.amazon.com',
    currency: { code: 'USD', symbol: '$', decimals: 2 },
    postalCode: '77042',
    flag: '?��?��',
  },
  UK: {
    code: 'UK',
    name: 'United Kingdom',
    domain: 'www.amazon.co.uk',
    currency: { code: 'GBP', symbol: '£', decimals: 2 },
    postalCode: 'SW1A 1AA',
    flag: '?��?��',
  },
  JP: {
    code: 'JP',
    name: 'Japan',
    domain: 'www.amazon.co.jp',
    currency: { code: 'JPY', symbol: '¥', decimals: 0 },
    postalCode: '100-0001',
    flag: '?��?��',
  },
  CA: {
    code: 'CA',
    name: 'Canada',
    domain: 'www.amazon.ca',
    currency: { code: 'CAD', symbol: 'C$', decimals: 2 },
    postalCode: 'M5H 2N2',
    flag: '🇨🇦',
  },
};

// Detect market from URL
function detectMarketFromUrl(url) {
  if (!url) return 'US';
  const urlLower = url.toLowerCase();
  if (urlLower.includes('amazon.co.uk')) return 'UK';
  if (urlLower.includes('amazon.co.jp')) return 'JP';
  if (urlLower.includes('amazon.ca')) return 'CA';
  return 'US';
}

// Sortable Table Row Component
function SortableTableRow({ category, index, market, selected, onSelect, onToggle, onEdit, onDelete, onUrlClick }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: category.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    backgroundColor: isDragging ? '#f5f5f5' : selected ? '#e3f2fd' : 'inherit',
  };

  return (
    <TableRow ref={setNodeRef} style={style}>
      <TableCell padding="checkbox">
        <Checkbox
          checked={selected}
          onChange={() => onSelect(category.id)}
        />
      </TableCell>
      <TableCell sx={{ cursor: 'grab', width: 40 }} {...attributes} {...listeners}>
        <DragIndicatorIcon color="action" />
      </TableCell>
      <TableCell align="center">
        <Chip
          label={index + 1}
          size="small"
          sx={{
            backgroundColor: '#FF6A00',
            color: 'white',
            fontWeight: 'bold',
            minWidth: 32
          }}
        />
      </TableCell>
      <TableCell>
        <Tooltip title={`${market.name} (${market.currency.symbol})`}>
          <Chip
            icon={<MarketFlag marketCode={market.code} size={16} />}
            label={market.code}
            size="small"
            sx={{
              fontWeight: 'bold',
              backgroundColor:
                market.code === 'US' ? '#e3f2fd' :
                market.code === 'UK' ? '#fce4ec' :
                market.code === 'JP' ? '#fff3e0' :
                market.code === 'CA' ? '#e8f5e9' : '#e3f2fd'
            }}
          />
        </Tooltip>
      </TableCell>
      <TableCell>{category.name}</TableCell>
      <TableCell>
        <Chip label={category.category_type || '-'} size="small" />
      </TableCell>
      <TableCell>
        <Link
          component="button"
          variant="body2"
          onClick={() => onUrlClick(category)}
          sx={{
            maxWidth: 200,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            display: 'block',
            textAlign: 'left',
            cursor: 'pointer',
          }}
        >
          {category.amazon_url}
        </Link>
      </TableCell>
      <TableCell align="center">{category.products_limit}</TableCell>
      <TableCell align="center">{category.priority}</TableCell>
      <TableCell align="center">
        <Switch
          checked={Boolean(category.is_enabled)}
          onChange={() => onToggle(category.id)}
          color="primary"
        />
      </TableCell>
      <TableCell align="center">
        <IconButton
          size="small"
          onClick={() => onEdit(category)}
          color="primary"
        >
          <EditIcon />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => onDelete(category.id)}
          color="error"
        >
          <DeleteIcon />
        </IconButton>
      </TableCell>
    </TableRow>
  );
}

function ScraperConfig() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [selectedMarketTab, setSelectedMarketTab] = useState('ALL');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [batchDialogOpen, setBatchDialogOpen] = useState(false);
  const [urlDialogOpen, setUrlDialogOpen] = useState(false);
  const [selectedUrlCategory, setSelectedUrlCategory] = useState(null);
  const [batchFormData, setBatchFormData] = useState({
    products_limit: '',
    is_enabled: '',
  });
  const [formData, setFormData] = useState({
    name: '',
    name_zh: '',
    category_type: '',
    amazon_url: '',
    market_code: 'US',
    postal_code: '77042',
    products_limit: 200,
    priority: 100,
    is_enabled: true,
  });
  const [successMessage, setSuccessMessage] = useState(null);

  // Show success notification
  const showSuccess = (message) => {
    setSuccessMessage(message);
    // Log to backend
    axios.post('/api/scraper-config/log-action', { action: message }).catch(() => {});
  };

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Fetch category list
  const fetchCategories = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get('/api/scraper-config/categories');
      if (response.data.code === 'OK') {
        setCategories(response.data.data);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch categories');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  // Auto-detect market when URL changes
  const handleUrlChange = (url) => {
    const detectedMarket = detectMarketFromUrl(url);
    const marketInfo = MARKETS[detectedMarket] || MARKETS.US;
    setFormData({
      ...formData,
      amazon_url: url,
      market_code: detectedMarket,
      postal_code: marketInfo.postalCode,
    });
  };

  // Filter categories by market tab
  const filteredCategories = useMemo(() => {
    const filtered = selectedMarketTab === 'ALL'
      ? categories
      : categories.filter(cat => (cat.market_code || 'US') === selectedMarketTab);
    return [...filtered].sort((a, b) => a.priority - b.priority);
  }, [categories, selectedMarketTab]);

  // Count categories by market
  const marketCounts = {
    ALL: categories.length,
    US: categories.filter(c => (c.market_code || 'US') === 'US').length,
    UK: categories.filter(c => c.market_code === 'UK').length,
    JP: categories.filter(c => c.market_code === 'JP').length,
    CA: categories.filter(c => c.market_code === 'CA').length,
  };

  // Selection handlers
  const handleSelectAll = () => {
    if (selectedIds.size === filteredCategories.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredCategories.map(c => c.id)));
    }
  };

  const handleSelect = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  // Drag end handler
  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = filteredCategories.findIndex(c => c.id === active.id);
      const newIndex = filteredCategories.findIndex(c => c.id === over.id);
      const movedCategory = filteredCategories.find(c => c.id === active.id);

      const reordered = arrayMove(filteredCategories, oldIndex, newIndex);
      const orderedIds = reordered.map(c => c.id);

      // Optimistic update
      setCategories(prev => {
        const newCategories = [...prev];
        reordered.forEach((cat, idx) => {
          const found = newCategories.find(c => c.id === cat.id);
          if (found) found.priority = idx + 1;
        });
        return newCategories;
      });

      // Save to backend
      try {
        await axios.put('/api/scraper-config/categories/reorder', {
          orderedIds,
          marketCode: selectedMarketTab
        });
        // Log reorder action
        const marketLabel = selectedMarketTab === 'ALL' ? 'All Markets' : selectedMarketTab;
        showSuccess(`Reordered "${movedCategory?.name}" from #${oldIndex + 1} to #${newIndex + 1} (${marketLabel})`);
      } catch (err) {
        setError('Failed to save order. Please refresh.');
        fetchCategories();
      }
    }
  };

  // Toggle enabled status
  const handleToggleEnabled = async (id) => {
    const category = categories.find(c => c.id === id);
    const newStatus = !category?.is_enabled;

    // Optimistic update
    setCategories(prev => prev.map(cat =>
      cat.id === id ? { ...cat, is_enabled: newStatus } : cat
    ));

    try {
      await axios.patch(`/api/scraper-config/categories/${id}/toggle`);
      showSuccess(`"${category?.name}" ${newStatus ? 'enabled' : 'disabled'}`);
    } catch (err) {
      // Revert on error
      setCategories(prev => prev.map(cat =>
        cat.id === id ? { ...cat, is_enabled: !newStatus } : cat
      ));
      setError(err.response?.data?.message || 'Failed to toggle status');
    }
  };

  // Delete category
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;

    // Save for potential rollback
    const deletedCategory = categories.find(cat => cat.id === id);

    // Optimistic update
    setCategories(prev => prev.filter(cat => cat.id !== id));
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      newSet.delete(id);
      return newSet;
    });

    try {
      await axios.delete(`/api/scraper-config/categories/${id}`);
      showSuccess(`Deleted "${deletedCategory?.name}"`);
    } catch (err) {
      // Revert on error
      if (deletedCategory) {
        setCategories(prev => [...prev, deletedCategory].sort((a, b) => a.priority - b.priority));
      }
      setError(err.response?.data?.message || 'Failed to delete');
    }
  };

  // Open edit dialog
  const handleEdit = (category) => {
    setEditingCategory(category);
    const marketCode = category.market_code || 'US';
    const defaultPostalCode = MARKETS[marketCode]?.postalCode || '77042';
    setFormData({
      name: category.name,
      name_zh: category.name_zh || '',
      category_type: category.category_type || '',
      amazon_url: category.amazon_url,
      market_code: marketCode,
      postal_code: category.postal_code || defaultPostalCode,
      products_limit: category.products_limit,
      priority: category.priority,
      is_enabled: category.is_enabled,
    });
    setOpenDialog(true);
  };

  // Open add dialog
  const handleAdd = () => {
    setEditingCategory(null);
    const defaultMarket = selectedMarketTab === 'ALL' ? 'US' : selectedMarketTab;
    const defaultPostalCode = MARKETS[defaultMarket]?.postalCode || '77042';
    setFormData({
      name: '',
      name_zh: '',
      category_type: '',
      amazon_url: '',
      market_code: defaultMarket,
      postal_code: defaultPostalCode,
      products_limit: 200,
      priority: 100,
      is_enabled: true,
    });
    setOpenDialog(true);
  };

  // Save category
  const handleSave = async () => {
    try {
      if (editingCategory) {
        // Optimistic update for edit
        const updatedCategory = { ...editingCategory, ...formData };
        setCategories(prev => prev.map(cat =>
          cat.id === editingCategory.id ? updatedCategory : cat
        ));
        setOpenDialog(false);

        await axios.put(`/api/scraper-config/categories/${editingCategory.id}`, formData);
        showSuccess(`Updated "${formData.name}"`);
      } else {
        // For new category, we need to fetch to get the new ID
        setOpenDialog(false);
        const response = await axios.post('/api/scraper-config/categories', formData);
        if (response.data.code === 'OK' && response.data.data) {
          setCategories(prev => [...prev, response.data.data].sort((a, b) => a.priority - b.priority));
        } else {
          fetchCategories();
        }
        showSuccess(`Added "${formData.name}"`);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save');
      fetchCategories(); // Revert on error
    }
  };

  // Batch update
  const handleBatchUpdate = async () => {
    const updates = {};
    if (batchFormData.products_limit !== '') {
      updates.products_limit = parseInt(batchFormData.products_limit);
    }
    if (batchFormData.is_enabled !== '' && batchFormData.is_enabled !== '-- Keep Current --') {
      // Handle both "true"/"false" and "Enabled"/"Disabled" values
      updates.is_enabled = batchFormData.is_enabled === 'true' || batchFormData.is_enabled === 'Enabled';
    }

    if (Object.keys(updates).length === 0) {
      setError('Please select at least one field to update');
      return;
    }

    // Save original state for potential rollback
    const originalCategories = [...categories];
    const idsToUpdate = Array.from(selectedIds);

    // Optimistic update
    setCategories(prev => prev.map(cat =>
      idsToUpdate.includes(cat.id) ? { ...cat, ...updates } : cat
    ));
    setBatchDialogOpen(false);
    setBatchFormData({ products_limit: '', is_enabled: '' });
    setSelectedIds(new Set());

    try {
      await axios.put('/api/scraper-config/categories/batch', {
        ids: idsToUpdate,
        updates
      });
      const changeDetails = Object.entries(updates)
        .map(([k, v]) => `${k === 'products_limit' ? 'limit' : k}=${v}`)
        .join(', ');
      showSuccess(`Batch updated ${idsToUpdate.length} categories (${changeDetails})`);
    } catch (err) {
      // Revert on error
      setCategories(originalCategories);
      setError(err.response?.data?.message || 'Failed to batch update');
    }
  };

  // URL Dialog handlers
  const handleUrlClick = (category) => {
    setSelectedUrlCategory(category);
    setUrlDialogOpen(true);
  };

  const handleCopyUrl = () => {
    if (selectedUrlCategory?.amazon_url) {
      navigator.clipboard.writeText(selectedUrlCategory.amazon_url);
    }
  };

  // Get market info for display
  const getMarketInfo = (marketCode) => {
    return MARKETS[marketCode] || MARKETS.US;
  };

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h5" gutterBottom>
            Scraper Category Configuration {categories.length > 0 && `(${categories.length})`}
          </Typography>
          <Stack direction="row" spacing={1}>
            {selectedIds.size > 0 && (
              <Button
                variant="contained"
                color="secondary"
                onClick={() => setBatchDialogOpen(true)}
              >
                Batch Edit ({selectedIds.size})
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchCategories}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAdd}
            >
              Add Category
            </Button>
          </Stack>
        </Stack>

        {/* Market Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs
            value={selectedMarketTab}
            onChange={(e, newValue) => {
              setSelectedMarketTab(newValue);
              setSelectedIds(new Set());
            }}
            textColor="primary"
            indicatorColor="primary"
          >
            <Tab
              value="ALL"
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>All Markets</span>
                  <Chip label={loading ? '-' : marketCounts.ALL} size="small" />
                </Box>
              }
            />
            <Tab
              value="US"
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FlagUS size={20} />
                  <span>US</span>
                  <Chip label={loading ? '-' : marketCounts.US} size="small" color={!loading && marketCounts.US > 0 ? 'primary' : 'default'} />
                </Box>
              }
            />
            <Tab
              value="UK"
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FlagUK size={20} />
                  <span>UK</span>
                  <Chip label={loading ? '-' : marketCounts.UK} size="small" color={!loading && marketCounts.UK > 0 ? 'primary' : 'default'} />
                </Box>
              }
            />
            <Tab
              value="JP"
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FlagJP size={20} />
                  <span>JP</span>
                  <Chip label={loading ? '-' : marketCounts.JP} size="small" color={!loading && marketCounts.JP > 0 ? 'primary' : 'default'} />
                </Box>
              }
            />
            <Tab
              value="CA"
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FlagCA size={20} />
                  <span>CA</span>
                  <Chip label={loading ? '-' : marketCounts.CA} size="small" color={!loading && marketCounts.CA > 0 ? 'primary' : 'default'} />
                </Box>
              }
            />
          </Tabs>
        </Box>

        {/* Drag hint */}
        <Alert severity="info" sx={{ mb: 2 }}>
          Drag rows to reorder priority. Select multiple rows for batch editing.
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <GoldIngotLoader minHeight={300} />
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedIds.size === filteredCategories.length && filteredCategories.length > 0}
                        indeterminate={selectedIds.size > 0 && selectedIds.size < filteredCategories.length}
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                    <TableCell sx={{ width: 40 }}></TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Market</TableCell>
                    <TableCell>Category Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>URL</TableCell>
                    <TableCell align="center">Scrape Count</TableCell>
                    <TableCell align="center">Priority</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <SortableContext
                    items={filteredCategories.map(c => c.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {filteredCategories.map((category, index) => {
                      const market = getMarketInfo(category.market_code);
                      return (
                        <SortableTableRow
                          key={category.id}
                          category={category}
                          index={index}
                          market={market}
                          selected={selectedIds.has(category.id)}
                          onSelect={handleSelect}
                          onToggle={handleToggleEnabled}
                          onEdit={handleEdit}
                          onDelete={handleDelete}
                          onUrlClick={handleUrlClick}
                        />
                      );
                    })}
                  </SortableContext>
                </TableBody>
              </Table>
            </TableContainer>
          </DndContext>
        )}
      </Paper>

      {/* Edit/Add Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingCategory ? 'Edit Category' : 'Add Category'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Category Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Category Type"
              value={formData.category_type}
              onChange={(e) => setFormData({ ...formData, category_type: e.target.value })}
              fullWidth
            />
            <TextField
              label="Amazon URL"
              value={formData.amazon_url}
              onChange={(e) => handleUrlChange(e.target.value)}
              fullWidth
              required
              helperText="Complete Amazon Best Sellers URL (Market auto-detected)"
            />

            {formData.amazon_url && (
              <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Detected Market Configuration:
                </Typography>
                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                  <Chip
                    icon={<MarketFlag marketCode={formData.market_code} size={16} />}
                    label={formData.market_code}
                    color="primary"
                  />
                  <Typography variant="body2">
                    Currency: {getMarketInfo(formData.market_code).currency.symbol} ({getMarketInfo(formData.market_code).currency.code})
                  </Typography>
                </Stack>
              </Box>
            )}

            <TextField
              label="Postal Code"
              value={formData.postal_code}
              onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
              fullWidth
              helperText={`Auto-detected default: ${getMarketInfo(formData.market_code).postalCode}`}
            />

            <TextField
              label="Products Limit"
              type="number"
              value={formData.products_limit}
              onChange={(e) => setFormData({ ...formData, products_limit: parseInt(e.target.value) })}
              fullWidth
            />
            <TextField
              label="Priority"
              type="number"
              value={formData.priority}
              onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) })}
              fullWidth
              helperText="Lower numbers have higher priority"
            />
            <Box>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Enabled Status
              </Typography>
              <Switch
                checked={formData.is_enabled}
                onChange={(e) => setFormData({ ...formData, is_enabled: e.target.checked })}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Batch Edit Dialog */}
      <Dialog open={batchDialogOpen} onClose={() => setBatchDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Batch Edit ({selectedIds.size} categories selected)
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Products Limit"
              type="number"
              value={batchFormData.products_limit}
              onChange={(e) => setBatchFormData({ ...batchFormData, products_limit: e.target.value })}
              fullWidth
              helperText="Leave empty to keep current value"
            />
            <TextField
              label="Enabled Status"
              select
              value={batchFormData.is_enabled}
              onChange={(e) => setBatchFormData({ ...batchFormData, is_enabled: e.target.value })}
              fullWidth
              SelectProps={{ native: true }}
              helperText="Leave empty to keep current value"
            >
              <option value="">-- Keep Current --</option>
              <option value="true">Enabled</option>
              <option value="false">Disabled</option>
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBatchDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleBatchUpdate} variant="contained" color="secondary">
            Update All Selected
          </Button>
        </DialogActions>
      </Dialog>

      {/* URL Display Dialog */}
      <Dialog open={urlDialogOpen} onClose={() => setUrlDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Amazon URL - {selectedUrlCategory?.name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Category: {selectedUrlCategory?.name}
            </Typography>
            <Typography variant="subtitle2" gutterBottom>
              Market: {selectedUrlCategory?.market_code || 'US'}
            </Typography>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                mt: 2,
                backgroundColor: '#f5f5f5',
                wordBreak: 'break-all',
              }}
            >
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                {selectedUrlCategory?.amazon_url}
              </Typography>
            </Paper>
            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                startIcon={<ContentCopyIcon />}
                onClick={handleCopyUrl}
              >
                Copy URL
              </Button>
              <Button
                variant="contained"
                startIcon={<OpenInNewIcon />}
                href={selectedUrlCategory?.amazon_url}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open in New Tab
              </Button>
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUrlDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={Boolean(successMessage)}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSuccessMessage(null)}
          severity="success"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default ScraperConfig;
